import { ENTITY_TYPES } from '@sales-center/common'
import { adminRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { aggregatePositionsByTeamId, selectAllPositions } from '../slice'
import { ISalesTeamPosition } from '../typings'
import { useSelectUsersForAssignment } from './useSelectUsersForAssignment'

export interface OwnerMenuItem {
  id: string
  name: string
  isPosition: boolean
  level: number
}

export const useGetOwnerList = (
  entityType: ENTITY_TYPES,
  salesTeamId: number,
  showInactiveUsers?: boolean,
  alwaysIncludeUserId?: string,
) => {
  const salesTeamsUsers = useSelectUsersForAssignment(
    entityType,
    salesTeamId,
    showInactiveUsers,
    alwaysIncludeUserId,
  )

  const positions = useSelector(selectAllPositions)
  return useMemo(() => {
    if (!salesTeamsUsers) return []
    const newItem = JSON.parse(
      JSON.stringify(aggregatePositionsByTeamId(salesTeamId, positions)),
    )
    const teamPositions: ISalesTeamPosition[] = [
      ...newItem,
      {
        systemCreated: true,
        id: 0,
        title: adminRoleName,
        parentId: null,
        positionAssignmentsCount: 0,
        positionUsers: [],
        salesTeamId: salesTeamId,
      },
    ].sort((a, b) => (a.id! > b.id! ? 1 : -1))

    if (teamPositions.length > 0 && salesTeamsUsers.length == 0) return []

    teamPositions.forEach(
      (x) =>
        (x.positionUsers = salesTeamsUsers
          .filter((i) => i.positionId === x.id)
          .map((x) => {
            return {
              userId: x.userId,
              userName: x.name,
            }
          })),
    )
    teamPositions.find(
      (x) => x.systemCreated && x.title === adminRoleName,
    )!.positionUsers = salesTeamsUsers
      .filter((i) => i.positionName === adminRoleName)
      .map((x) => {
        return {
          userId: x.userId,
          userName: x.name,
        }
      })
    const result = loadOwnerList(teamPositions)

    return result
  }, [salesTeamsUsers, positions, salesTeamId])
}

const loadOwnerList = (array: ISalesTeamPosition[]) => {
  //adding admin role users to the bottom of the list
  const adminIndex = array.findIndex((item) => item.title === adminRoleName)
  if (adminIndex !== -1) {
    const [adminItem] = array.splice(adminIndex, 1)
    array.push(adminItem)
  }

  if (array.length == 0) return []
  const noParentPositions = array.filter((i) => i.parentId === null)
  const allChildPositions = array.filter((i) => i.parentId !== null)
  let i: number = 0
  const level = -1
  const result: OwnerMenuItem[] = []
  while (i <= noParentPositions.length - 1) {
    const curr: ISalesTeamPosition = noParentPositions[i]
    loadOwnerListLevel(allChildPositions, result, curr, level)
    i++
  }
  return result
}

const loadOwnerListLevel = (
  array: ISalesTeamPosition[],
  muttableResultsAccumulator: OwnerMenuItem[],
  current: ISalesTeamPosition,
  level: number,
) => {
  if (!hasUsersOrSubUsers(current, array)) {
    return
  }

  level++
  muttableResultsAccumulator.push({
    id: current.id!.toString(),
    name: current.title,
    isPosition: true,
    level: level,
  })

  if (current.positionUsers.length > 0) {
    level++
    current.positionUsers.forEach((x) =>
      muttableResultsAccumulator.push({
        id: x.userId,
        name: x.userName,
        isPosition: false,
        level: level,
      }),
    )
  }

  array
    .filter((i) => i.parentId === current.id)
    .forEach((x) =>
      loadOwnerListLevel(array, muttableResultsAccumulator, x, level),
    )
}

const hasUsersOrSubUsers = (
  position: ISalesTeamPosition,
  allPositions: ISalesTeamPosition[],
): boolean => {
  if (position.positionUsers.length > 0) {
    return true
  }

  const subPositions = allPositions.filter((p) => p.parentId === position.id)
  return subPositions.some((subPosition) =>
    hasUsersOrSubUsers(subPosition, allPositions),
  )
}
